/**
 * Per-follow threshold-alert config stored under `data.thresholdAlert` on a `savedCompany#*`
 * UserState item — lets a user ask to be notified when a followed company's employer score
 * drops below, or its open-roles count rises above, a value they pick (distinct from the
 * always-on "new roles" digest section).
 *
 * `notifiedAt` implements edge-triggered notification: null means "armed" (the condition may
 * be breached or not, but no notification has gone out for the current breach); a timestamp
 * means "already notified for this breach" and is cleared back to null once the metric moves
 * back across the threshold, so the next breach can notify again. See
 * lambda/alerts/evaluate-threshold-breaches.ts for the state-machine logic.
 */

export const THRESHOLD_METRICS = ['employerScore', 'openRoles'] as const;
export type ThresholdMetric = (typeof THRESHOLD_METRICS)[number];

export const THRESHOLD_CONDITIONS = ['below', 'above'] as const;
export type ThresholdCondition = (typeof THRESHOLD_CONDITIONS)[number];

export interface ThresholdAlertConfig {
  metric: ThresholdMetric;
  condition: ThresholdCondition;
  value: number;
  /** Epoch ms of the last notification for the metric's current breach; null when armed. */
  notifiedAt: number | null;
}

export function isThresholdMetric(value: unknown): value is ThresholdMetric {
  return typeof value === 'string' && (THRESHOLD_METRICS as readonly string[]).includes(value);
}

export function isThresholdCondition(value: unknown): value is ThresholdCondition {
  return typeof value === 'string' && (THRESHOLD_CONDITIONS as readonly string[]).includes(value);
}

export function parseThresholdAlertConfig(value: unknown): ThresholdAlertConfig | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (!isThresholdMetric(record.metric)) return null;
  if (!isThresholdCondition(record.condition)) return null;
  if (typeof record.value !== 'number' || !Number.isFinite(record.value)) return null;
  if (record.notifiedAt !== undefined && record.notifiedAt !== null && typeof record.notifiedAt !== 'number') return null;
  return {
    metric: record.metric,
    condition: record.condition,
    value: record.value,
    notifiedAt: record.notifiedAt ?? null,
  };
}
